import { ReactElement, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  Typography,
  Paper,
  useTheme
} from '@mui/material';
import { TypographyProps } from '@mui/material/Typography';
import { AgentFees, Currency, Passenger, PassengerPricing, PassengerType } from '~/types';
import { ExpandCircleDown } from '@mui/icons-material';
import { getCurrencySign, getTotalBaggageCartPrice } from '~/modules/helpers';
import { BOOKING_CONTEXT } from '~/literals';

interface FlightBookingPriceSummaryProps {
  passengerPricing: PassengerPricing[];
  passengers: Passenger[];
  currency: string;
  agentFeesOnly: boolean;
  agentFees: AgentFees;
  bookingContext: string;
}

const FlightBookingPriceSummary = ({ passengerPricing, agentFees, currency, agentFeesOnly, passengers, bookingContext }: FlightBookingPriceSummaryProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const totalBaggagePrice = getTotalBaggageCartPrice(passengers);
  const theme = useTheme();
  currency = getCurrencySign(currency as Currency);


  const getTotal = (pricing: PassengerPricing[], withBaggage?: boolean) => {
    if (agentFeesOnly) return agentFees.total;
    let totalFlightPrice = pricing.reduce((acc, curr) => acc + parseFloat(curr.price.total), 0);
    totalFlightPrice += withBaggage ? Number(totalBaggagePrice) + parseFloat(agentFees.total) : parseFloat(agentFees.total);
    return totalFlightPrice.toFixed(2);
  };

  const getBaseTotal = (pricing: PassengerPricing[]) => {
    return pricing.reduce((acc, curr) => acc + parseFloat(curr.price.base), 0).toFixed(2);
  };

  const getTaxesTotal = (pricing: PassengerPricing[]) => {
    const gdsFees = pricing.reduce((acc, curr) => {
      const taxes = curr.price.taxes ? curr.price.taxes.reduce((taxAcc, taxCurr) => taxAcc + parseFloat(taxCurr.amount), 0) : 0;
      return acc + taxes;
    }, 0)
    return (gdsFees + parseFloat(agentFees.total)).toFixed(2);
  };

  const getNumberOfPassengersByType = (passengerType: PassengerType): number => {
    return passengerPricing.filter(pass => pass.passengerType.toLowerCase().includes(passengerType.toLowerCase())).length;
  }

  const getItemPrice = (description: string, price: string, typographyVariant?: TypographyProps['variant'], styles?: Record<string, any>): ReactElement => {
    return (
      <>
        <Grid item xs={6}>
          <Typography variant={typographyVariant || "body1"} sx={{ ...styles }}>
            {description}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant={typographyVariant || "body1"} sx={{ textAlign: 'right', ...styles }}>
            {currency}{price}
          </Typography>
        </Grid>
      </>
    );
  }

  let adultsSummary;
  let childrenSummary;
  let infantsSummary;


  let adultsDetails;
  let childrenDetails;
  let infantsDetails;

  if (passengerPricing.length) {
    const numberOfAdults = getNumberOfPassengersByType(PassengerType.ADULT);
    const numberOfChildred = getNumberOfPassengersByType(PassengerType.CHILD);
    const numberOfInfants = getNumberOfPassengersByType(PassengerType.INFANT);

    if (numberOfAdults) {
      adultsSummary = getItemPrice(
        numberOfAdults > 1 ? `${numberOfAdults} x Adults` : `1 x Adult`,
        getTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.ADULT.toLocaleLowerCase()))));

      adultsDetails = (
        <>

          {adultsSummary}
          {
            getItemPrice(
              'Base',
              getBaseTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.ADULT.toLocaleLowerCase()))),
              'body2'
            )
          }
          {
            getItemPrice(
              'Taxes and Fees',
              getTaxesTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.ADULT.toLocaleLowerCase()))),
              'body2',
              { marginBottom: 3 }
            )
          }
        </>
      )
    }
    if (numberOfChildred) {
      childrenSummary = getItemPrice(
        numberOfChildred > 1 ? `${numberOfChildred} x Children` : `1 x Child`,
        getTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.CHILD.toLocaleLowerCase()))));

      childrenDetails = (
        <>

          {childrenSummary}
          {
            getItemPrice(
              'Base',
              getBaseTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.CHILD.toLocaleLowerCase()))),
              'body2'
            )
          }
          {
            getItemPrice(
              'Taxes and Fees',
              getTaxesTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.CHILD.toLocaleLowerCase()))),
              'body2',
              { marginBottom: 3 }
            )
          }
        </>
      )
    }
    if (numberOfInfants) {
      infantsSummary = getItemPrice(
        numberOfInfants > 1 ? `"${numberOfInfants} x Infants` : `1 x Infant`,
        getTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.INFANT.toLocaleLowerCase()))));

      infantsDetails = (
        <>
          {infantsSummary}
          {
            getItemPrice(
              'Base',
              getBaseTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.INFANT.toLocaleLowerCase()))),
              'body2'
            )
          }
          {
            getItemPrice(
              'Taxes and Fees',
              getTaxesTotal(passengerPricing.filter(pass => pass.passengerType.toLocaleLowerCase().includes(PassengerType.INFANT.toLocaleLowerCase()))),
              'body2'
            )
          }
        </>
      )
    }
  }


  const handleToggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 5, bgcolor: theme.palette.secondary.main, color: '#fff' }}>
      <Typography variant="h5" gutterBottom>
        Summary
      </Typography>
      <Grid container spacing={.5}>
        <Grid item xs={6}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Total:
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'right' }}>
            {currency}{getTotal(passengerPricing, true)}
          </Typography>
        </Grid>
      </Grid>
      <Collapse in={showDetails && bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY} timeout="auto" unmountOnExit>
        <Box mt={2}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Price Breakdown
          </Typography>
          <Grid container spacing={0}>
            <Grid item xs={6}>
              <Typography variant="body1">
                Total:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" sx={{ textAlign: 'right' }}>
                {currency}{getTotal(passengerPricing, true)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">Base</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ textAlign: 'right' }}>{currency}{getBaseTotal(passengerPricing)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">Taxes and Fees</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ textAlign: 'right', marginBottom: totalBaggagePrice === '0.00' ? 3 : 0 }}>{currency}{getTaxesTotal(passengerPricing)}</Typography>
            </Grid>
            {
              totalBaggagePrice !== '0.00' &&
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Baggage</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ textAlign: 'right', marginBottom: 3 }}>{currency}{totalBaggagePrice}</Typography>
                </Grid>
              </>
            }
            {
              passengerPricing.length > 1 &&
              <>
                <hr style={{ width: '100%', border: '1px solid #fff', margin: '10px 0' }} />
                {adultsDetails}
                {childrenDetails}
                {infantsDetails}
              </>
            }
          </Grid>

        </Box>
      </Collapse>
      {
        bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY
        &&
        <Button
          variant="text"
          color="secondary"
          endIcon={showDetails ? <ExpandCircleDown sx={{ transform: 'rotate(180deg)' }} /> : <ExpandCircleDown />}
          onClick={handleToggleDetails}
          sx={{ mt: 2, textTransform: 'capitalize' }}
        >
          {showDetails ? 'Close price breakdown' : 'View price breakdown'}
        </Button>
      }
    </Paper>
  );
};

export default FlightBookingPriceSummary;